import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles as SparklesIcon } from "lucide-react";
import Sparkles from "../components/Sparkles";
import LangToggle from "../components/LangToggle";
import { fileUrl, getFeaturedPhoto, listPhotos } from "../lib/api";
import { useLang } from "../lib/i18n";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };
const PHOTO_DURATION = 6500;

// Six 3D transition effects cycled per slide
const FX = ["fxFlip", "fxCube", "fxBookOpen", "fxZoomTilt", "fxSwingIn", "fxCardSlide"];

export default function Memories({ embed }) {
  const lang = useLang();
  const isEmbed = embed === true || (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embed") === "1");
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [cycleKey, setCycleKey] = useState(0);
  const photosRef = useRef([]);
  photosRef.current = photos;
  const seenFeatureRef = useRef(undefined);

  useEffect(() => {
    const load = async () => {
      try {
        const ps = await listPhotos();
        setPhotos(ps || []);
      } catch { /* ignore */ }
    };
    load();
    const tm = setInterval(load, 20000);
    return () => clearInterval(tm);
  }, []);

  // Admin "Show Now" — poll the featured photo and jump to it immediately.
  useEffect(() => {
    const poll = async () => {
      try {
        const f = await getFeaturedPhoto();
        const stamp = f?.featured_at || null;
        if (seenFeatureRef.current === undefined) {
          seenFeatureRef.current = stamp;
          return;
        }
        if (!stamp || stamp === seenFeatureRef.current) return;
        seenFeatureRef.current = stamp;
        let list = photosRef.current;
        let i = list.findIndex((p) => p.id === f.photo_id);
        if (i < 0) {
          list = (await listPhotos()) || [];
          setPhotos(list);
          i = list.findIndex((p) => p.id === f.photo_id);
        }
        if (i >= 0) {
          setIdx(i);
          setCycleKey((k) => k + 1); // restart the rotation timer
        }
      } catch { /* ignore */ }
    };
    poll();
    const tm = setInterval(poll, 3000);
    return () => clearInterval(tm);
  }, []);

  useEffect(() => {
    if (photos.length < 2) return;
    const tm = setInterval(() => setIdx((i) => (i + 1) % photos.length), PHOTO_DURATION);
    return () => clearInterval(tm);
  }, [photos.length, cycleKey]);

  const current = photos[idx];
  const fx = FX[idx % FX.length];
  const currentSrc = current ? fileUrl(current.path) : null;

  return (
    <div className="fixed inset-0 overflow-hidden bg-black text-blush" data-testid="memories-slideshow">
      {/* Blurred copy of the current photo as cinematic backdrop */}
      {currentSrc && (
        <div
          key={`bg-${current.id}`}
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${currentSrc})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(28px) brightness(0.55) saturate(1.2)",
            transform: "scale(1.15)",
            transition: "background-image 1.2s ease",
          }}
        />
      )}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%), linear-gradient(180deg, rgba(252,235,170,0.08), rgba(74,22,53,0.12))",
        }}
      />
      <Sparkles count={50} />

      {/* Light leak ribbons */}
      <div className="absolute inset-0 pointer-events-none mix-blend-screen opacity-60">
        <div className="absolute -top-20 -left-20 w-[60vw] h-[40vw] rounded-full" style={{
          background: "radial-gradient(circle, rgba(255,210,150,0.35), transparent 70%)",
          animation: "lightDrift 14s ease-in-out infinite",
        }} />
        <div className="absolute -bottom-20 -right-20 w-[50vw] h-[40vw] rounded-full" style={{
          background: "radial-gradient(circle, rgba(252,201,228,0.30), transparent 70%)",
          animation: "lightDrift 18s ease-in-out infinite reverse",
        }} />
      </div>

      {/* The 3D photo card */}
      {current && (
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(88vw,90vh)] h-[min(88vh,90vw)] z-20"
          style={{ perspective: "1500px" }}
        >
          <div
            key={`card-${current.id}-${idx}`}
            className="relative w-full h-full"
            style={{
              transformStyle: "preserve-3d",
              animation: `${fx} 1100ms cubic-bezier(0.2, 0.7, 0.2, 1) forwards, gentleFloat 6s ease-in-out 1.1s infinite`,
            }}
          >
            <div
              className="absolute inset-0 rounded-[26px] pointer-events-none"
              style={{
                border: "10px solid",
                borderImage:
                  "linear-gradient(135deg, #FCF6BA 0%, #D4AF37 30%, #FCF6BA 50%, #D4AF37 70%, #8B6A1A 100%) 1",
                boxShadow:
                  "0 35px 90px rgba(0,0,0,0.65), 0 0 0 2px rgba(255,255,255,0.25) inset, 0 0 30px rgba(252,246,186,0.45)",
              }}
            />
            <div
              className="absolute pointer-events-none"
              style={{
                inset: 12,
                border: "1.5px solid rgba(252,246,186,0.55)",
                borderRadius: 18,
                boxShadow: "inset 0 0 24px rgba(252,246,186,0.25)",
              }}
            />
            {["tl", "tr", "bl", "br"].map((c) => (
              <svg
                key={c}
                width="56" height="56" viewBox="0 0 56 56"
                className="absolute"
                style={{
                  top: c.startsWith("t") ? 4 : "auto",
                  bottom: c.startsWith("b") ? 4 : "auto",
                  left: c.endsWith("l") ? 4 : "auto",
                  right: c.endsWith("r") ? 4 : "auto",
                  transform: `scale(${c.endsWith("r") ? -1 : 1}, ${c.startsWith("b") ? -1 : 1})`,
                }}
              >
                <g fill="none" stroke="#FCF6BA" strokeWidth="1.5" opacity="0.95">
                  <path d="M 8 26 Q 8 8 26 8" />
                  <circle cx="18" cy="18" r="3" fill="#FCF6BA" />
                  <path d="M 14 8 L 26 8 M 8 14 L 8 26" />
                </g>
              </svg>
            ))}
            <div className="absolute inset-[12px] rounded-[16px] overflow-hidden bg-black">
              <img
                src={currentSrc}
                alt={current.name || "memory"}
                className="w-full h-full"
                style={{ objectFit: "contain", objectPosition: "center" }}
                draggable={false}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.06) 100%)",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Top bar — Back + language only (no music controls). Hidden when embedded. */}
      {!isEmbed && (
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-30 pointer-events-none">
        <Link to="/welcome" className="glass rounded-full px-3 py-1.5 flex items-center gap-2 text-plum pointer-events-auto">
          <ArrowLeft size={14} /> <span className="label-eyebrow" style={TE}>
            {lang === "te" ? "తిరిగి" : "Back"}
          </span>
        </Link>
        <div className="pointer-events-auto">
          <LangToggle light />
        </div>
      </div>
      )}

      {/* Title */}
      <div className="absolute left-0 right-0 top-16 sm:top-20 text-center px-6 z-25 pointer-events-none">
        <p className="font-script gold-text drop-shadow-2xl" style={{ ...TE, fontSize: "clamp(26px, 5.5vw, 50px)" }}>
          {lang === "te" ? "అనఘ జ్ఞాపకాలు" : "Anagha's Memories"}
        </p>
        <p className="font-display text-blush/90 drop-shadow-lg" style={{ ...TE, fontSize: "clamp(12px, 2.4vw, 16px)" }}>
          <SparklesIcon size={12} className="inline -mt-1 mr-1 text-[#FCF6BA]" />
          {lang === "te" ? "మా చిన్నారి యువరాణి కథ" : "A little princess's story"}
        </p>
      </div>

      {/* Empty state */}
      {!photos.length && (
        <div className="absolute inset-0 grid place-items-center text-center px-6 z-30">
          <div>
            <SparklesIcon size={44} className="mx-auto text-[#FCF6BA] mb-3" />
            <p className="font-script text-blush" style={{ ...TE, fontSize: "clamp(22px, 5vw, 38px)" }}>
              {lang === "te" ? "ఇంకా జ్ఞాపకాలు చేర్చబడలేదు" : "No memories added yet"}
            </p>
            <p className="label-eyebrow text-blush/80 mt-2" style={TE}>
              {lang === "te" ? "నిర్వాహకుడు Admin నుండి ఫోటోలు అప్‌లోడ్ చేస్తారు" : "Admin can upload photos from the dashboard"}
            </p>
          </div>
        </div>
      )}

      {/* Progress dots */}
      {photos.length > 1 && (
        <div className="absolute left-0 right-0 bottom-6 flex justify-center gap-1.5 z-30 px-4">
          {photos.map((p, i) => (
            <span
              key={p.id}
              className="rounded-full transition-all"
              style={{
                width: i === idx ? 24 : 7, height: 7,
                background: i === idx ? "#FCF6BA" : "rgba(255,255,255,0.45)",
                boxShadow: i === idx ? "0 0 10px rgba(252,246,186,0.75)" : "none",
              }}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes fxFlip {
          0%   { opacity: 0; transform: rotateY(-95deg) scale(0.85); }
          60%  { opacity: 1; transform: rotateY(12deg) scale(1.02); }
          100% { opacity: 1; transform: rotateY(0deg) scale(1); }
        }
        @keyframes fxCube {
          0%   { opacity: 0; transform: rotateY(85deg) translateX(60%) scale(0.9); }
          100% { opacity: 1; transform: rotateY(0deg) translateX(0) scale(1); }
        }
        @keyframes fxBookOpen {
          0%   { opacity: 0; transform: rotateX(-78deg) translateY(28%) scale(0.85); transform-origin: bottom center; }
          100% { opacity: 1; transform: rotateX(0deg) translateY(0) scale(1); }
        }
        @keyframes fxZoomTilt {
          0%   { opacity: 0; transform: scale(0.4) rotateZ(-12deg) rotateY(20deg); }
          60%  { opacity: 1; transform: scale(1.06) rotateZ(2deg) rotateY(-4deg); }
          100% { opacity: 1; transform: scale(1) rotateZ(0) rotateY(0); }
        }
        @keyframes fxSwingIn {
          0%   { opacity: 0; transform: rotateZ(35deg) translate(-40%, -40%) scale(0.6); transform-origin: top right; }
          60%  { opacity: 1; transform: rotateZ(-6deg) translate(0,0) scale(1.04); }
          100% { opacity: 1; transform: rotateZ(0deg) scale(1); }
        }
        @keyframes fxCardSlide {
          0%   { opacity: 0; transform: translateX(100%) rotateY(-55deg) scale(0.8); }
          100% { opacity: 1; transform: translateX(0) rotateY(0deg) scale(1); }
        }
        @keyframes gentleFloat {
          0%,100% { transform: translateY(0) rotateZ(0) rotateY(0); }
          25%     { transform: translateY(-6px) rotateZ(0.4deg) rotateY(-1deg); }
          50%     { transform: translateY(0) rotateZ(-0.4deg) rotateY(1deg); }
          75%     { transform: translateY(-4px) rotateZ(0.3deg) rotateY(-0.6deg); }
        }
        @keyframes lightDrift {
          0%,100% { transform: translate(0,0) scale(1); }
          50%     { transform: translate(8%, 6%) scale(1.15); }
        }
      `}</style>
    </div>
  );
}
